/**
 * Saved event lists page (BUILD-SPEC-v3 section 6.2). Loaded on the lists page only.
 * Storage and share-link encoding live in theme.js (window.Botanique.lists).
 */
(() => {
  const B = window.Botanique;
  let s = {};
  try {
    s = JSON.parse(document.getElementById('ListsStrings').textContent);
  } catch (e) {
    /* keys fall back to empty */
  }
  const fmt = (template, values) =>
    Object.entries(values).reduce((out, [k, v]) => out.replaceAll(`[${k}]`, v).replaceAll(`{{ ${k} }}`, v), template || '');
  const say = (message) => {
    const region = document.getElementById('LiveRegion');
    if (!region) return;
    region.textContent = '';
    setTimeout(() => (region.textContent = message), 50);
  };
  const el = (tag, attrs = {}, ...children) => {
    const node = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) {
      if (k === 'class') node.className = v;
      else if (k === 'text') node.textContent = v;
      else if (k.startsWith('on')) node.addEventListener(k.slice(2), v);
      else node.setAttribute(k, v);
    }
    children.flat().forEach((c) => c && node.append(c));
    return node;
  };

  class SavedLists extends HTMLElement {
    async connectedCallback() {
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: this.dataset.currency || 'USD',
      });
      this.mine = this.querySelector('[data-lists-mine]');
      this.shared = this.querySelector('[data-lists-shared]');
      try {
        const index = await B.loadSkuIndex();
        this.byId = new Map(index.map((i) => [i.id, i]));
      } catch (e) {
        this.byId = new Map();
      }
      const code = new URLSearchParams(window.location.search).get('l');
      if (code) this.renderShared(code);
      this.render();
      document.addEventListener('lists:changed', () => this.render());
    }

    itemsLabel(n) {
      return fmt(n === 1 ? s.itemsOne : s.itemsOther, { count: n });
    }

    total(list) {
      return list.items.reduce((sum, x) => {
        const v = this.byId.get(x.v);
        return v && v.available ? sum + v.price * x.q : sum;
      }, 0);
    }

    renderItems(list, editable) {
      return el(
        'ul',
        { class: 'list-items', role: 'list' },
        list.items.map((x, i) => {
          const v = this.byId.get(x.v);
          if (!v) {
            return el(
              'li',
              { class: 'list-item is-unavailable' },
              el('span'),
              el('div', { class: 'list-item__info' }, el('span', { text: s.unavailable })),
              editable ? this.removeButton(list, i) : el('span'),
              el('span'),
            );
          }
          const perUnit = Math.round(v.price / (v.units || 1));
          const img = v.image ? el('img', { src: v.image, alt: '', width: '56', height: '70', loading: 'lazy' }) : el('span');
          const info = el(
            'div',
            { class: 'list-item__info' },
            el('a', { class: 'list-item__title', href: `/products/${v.handle}`, text: v.title }),
            el('span', { class: 'list-item__meta' }, el('span', { class: 'mono', text: v.sku }), ` · ${v.unit} · ${fmt(s.each, { price: this.money.format(perUnit / 100) })}`),
          );
          let qty;
          if (editable) {
            const id = `List-${list.id}-${i}`;
            const input = el('input', { class: 'qty__input', id, type: 'number', inputmode: 'numeric', min: '1', step: '1', value: String(x.q) });
            input.addEventListener('change', () => {
              const items = [...list.items];
              items[i] = { ...items[i], q: Math.max(1, parseInt(input.value, 10) || 1) };
              B.lists.update(list.id, { items });
            });
            qty = el('div', { class: 'qty' }, el('label', { class: 'visually-hidden', for: id, text: `${s.qty}, ${v.title}` }), input);
          } else {
            qty = el('span', { class: 'mono', text: `x ${x.q}` });
          }
          const line = el('span', { class: 'list-item__total price', text: v.available ? this.money.format((v.price * x.q) / 100) : s.unavailable });
          return el('li', { class: `list-item${v.available ? '' : ' is-unavailable'}` }, img, info, qty, editable ? el('div', { class: 'list-item__end' }, line, this.removeButton(list, i)) : line);
        }),
      );
    }

    removeButton(list, i) {
      return el('button', {
        type: 'button',
        class: 'link-button',
        text: s.remove,
        onclick: () => {
          const items = list.items.filter((_, n) => n !== i);
          B.lists.update(list.id, { items });
        },
      });
    }

    async addAll(list, button) {
      const items = list.items.filter((x) => this.byId.get(x.v)?.available).map((x) => ({ id: x.v, quantity: x.q }));
      if (!items.length) return;
      button.setAttribute('aria-disabled', 'true');
      try {
        const data = await B.cart.add(items);
        B.cart.render(data.sections);
        say(fmt(s.added, { count: items.length }));
        document.querySelector('cart-drawer')?.open(button);
      } catch (e) {
        say(e.message);
      } finally {
        button.removeAttribute('aria-disabled');
      }
    }

    quoteUrl(list) {
      const quote = B.routes?.quote;
      return quote ? `${quote}?l=${B.lists.encode(list)}&print=1` : null;
    }

    render() {
      const all = B.lists.all();
      this.mine.replaceChildren();
      if (!all.length) {
        this.mine.append(el('p', { text: s.empty }));
        return;
      }
      all.forEach((list) => this.mine.append(this.card(list, true)));
    }

    card(list, editable) {
      const headingId = `ListHeading-${list.id || 'shared'}`;
      let head;
      if (editable) {
        const nameId = `ListName-${list.id}`;
        const dateId = `ListDate-${list.id}`;
        const name = el('input', { class: 'input', id: nameId, value: list.name, maxlength: '80', placeholder: s.untitled });
        name.addEventListener('change', () => B.lists.update(list.id, { name: name.value.trim() }));
        const date = el('input', { class: 'input', id: dateId, type: 'date', value: list.date || '' });
        date.addEventListener('change', () => B.lists.update(list.id, { date: date.value }));
        head = el(
          'div',
          { class: 'list-card__head' },
          el(
            'div',
            { class: 'list-card__fields' },
            el('div', { class: 'field' }, el('label', { class: 'field__label', for: nameId, text: s.renameLabel }), name),
            el('div', { class: 'field' }, el('label', { class: 'field__label', for: dateId, text: s.dateLabel }), date),
          ),
          el('h2', { class: 'visually-hidden', id: headingId, text: list.name || s.untitled }),
        );
      } else {
        head = el('div', { class: 'stack' }, el('h2', { class: 'h2', id: headingId, text: list.name || s.untitled }), list.date ? el('p', { class: 'text-secondary', text: window.BotaniqueDelivery ? window.BotaniqueDelivery.formatDay(list.date) : list.date }) : null);
      }

      const addBtn = el('button', { type: 'button', class: 'button', text: s.addAll });
      addBtn.addEventListener('click', () => this.addAll(list, addBtn));
      const actions = el('div', { class: 'list-card__actions' }, addBtn);
      const quote = this.quoteUrl(list);
      if (quote) actions.append(el('a', { class: 'button button--secondary', href: quote, text: s.download }));
      if (editable) {
        actions.append(
          el('button', {
            type: 'button',
            class: 'button button--secondary',
            text: s.copyLink,
            onclick: async () => {
              const url = B.lists.shareUrl(list);
              try {
                await navigator.clipboard.writeText(url);
              } catch (e) {
                window.prompt(s.copyLink, url);
              }
              say(s.copied);
              B.publish?.('botanique:list_shared', { items: list.items.length });
            },
          }),
          el('button', {
            type: 'button',
            class: 'link-button',
            text: s.delete,
            onclick: () => {
              if (window.confirm(fmt(s.deleteConfirm, { list: list.name || s.untitled }))) B.lists.remove(list.id);
            },
          }),
        );
      }

      const foot = el(
        'div',
        { class: 'list-card__foot' },
        el('p', {}, `${this.itemsLabel(list.items.length)} · ${s.total} `, el('strong', { class: 'price', text: this.money.format(this.total(list) / 100) })),
        actions,
      );
      return el('section', { class: 'list-card', 'aria-labelledby': headingId }, head, this.renderItems(list, editable), foot);
    }

    renderShared(code) {
      let list;
      try {
        list = B.lists.decode(code);
      } catch (e) {
        return;
      }
      const save = el('button', {
        type: 'button',
        class: 'button button--secondary lists__save-shared',
        text: s.saveShared,
        onclick: () => {
          B.lists.create(list.name, list.date, list.items);
          this.shared.hidden = true;
          window.history.replaceState({}, '', window.location.pathname);
        },
      });
      this.shared.replaceChildren(
        el('p', { class: 'text-sm text-secondary', text: `${s.sharedTitle}. ${s.sharedNote}` }),
        this.card({ ...list, id: 'shared' }, false),
        save,
      );
      this.shared.hidden = false;
    }
  }

  customElements.define('saved-lists', SavedLists);
})();
